export const M9_UNIT_14 = {
  id: "m9-u14-real-unreal-situations",
  title: "Unidad 14: Real and Unreal Situations",
  topic: "Master the difference between real and hypothetical! Learn First and Second Conditionals to talk about possibilities and dreams. Perfect for business discussions, career planning, and professional scenarios!",
  iconType: "briefcase",
  grammarImage: "/grammar/grammar-m9-u14.jpg",
  grammarText: "First Conditional (If + present, will + verb) for REAL possibilities. Second Conditional (If + past, would + verb) for UNREAL/hypothetical situations. Plus business collocations with GIVE, HOLD, and SET!",
  
  sets: [
    {
      id: "m9-u14-s1",
      title: "Set 1: Introduction to First Conditional 🎯",
      context: `**Team Meeting - Planning Next Quarter**

Manager: "If we finish the project on time, we'll get the bonus. If we don't meet the deadline, the client won't be happy."`,
      
      questions: [
        {
          type: "mc",
          q: "The manager is talking about:",
          options: [
            "A real possibility that could happen",
            "An impossible dream",
            "Something that happened yesterday"
          ],
          a: "A real possibility that could happen",
          tip: "First conditional = real situations that are possible!",
          successtip: "✓ FIRST CONDITIONAL!\nUse it for REAL situations that can actually happen! If + present simple, will + verb! 🎯"
        },
        {
          type: "match",
          q: "Match the parts of the first conditional:",
          pairs: [
            { left: "If we finish on time,", right: "we'll get the bonus" },
            { left: "If we work hard,", right: "we'll succeed" },
            { left: "If it rains,", right: "I'll bring an umbrella" }
          ],
          a: "completed",
          successtip: "✓ FIRST CONDITIONAL STRUCTURE!\nCondition (If + present) → Result (will + verb)! Natural and logical! ✨"
        },
        {
          type: "input",
          q: "Complete: If we _____ (finish) early, we'll celebrate.",
          a: "finish",
          alts: [],
          tip: "After IF, use present simple (not will)!",
          successtip: "✓ NO WILL AFTER IF!\nCommon mistake! Use present simple after IF, even for future! 🚫will"
        },
        {
          type: "order",
          q: "Translate: Si trabajamos duro, tendremos éxito",
          words: ["If", "we", "work", "hard,", "we'll", "have", "success"],
          a: "If we work hard, we'll have success",
          extra: ["will", "worked"],
          tip: "Present after IF, will in the result!",
          successtip: "✓ PERFECT STRUCTURE!\nIf + present simple, will + base verb! This is the formula! 📐"
        },
        {
          type: "mc",
          q: "Which sentence is correct?",
          options: [
            "If I see her, I'll tell her",
            "If I will see her, I tell her",
            "If I saw her, I'll tell her"
          ],
          a: "If I see her, I'll tell her",
          successtip: "✓ CORRECT PATTERN!\nIf + present (see), will + base verb (tell)! Perfect first conditional! 🎯"
        },
        {
          type: "order",
          q: "Translate: No aprobaremos si no estudiamos",
          words: ["We", "won't", "pass", "if", "we", "don't", "study"],
          a: "We won't pass if we don't study",
          extra: ["will", "didn't"],
          tip: "You can reverse the order! No comma when result comes first!",
          successtip: "✓ REVERSED ORDER!\nResult first = no comma! If comes later in the sentence! Both ways work! 🔄"
        },
        {
          type: "input",
          q: "Complete: If the weather _____ (be) nice, we'll go to the park.",
          a: "is",
          alts: [],
          successtip: "✓ PRESENT SIMPLE!\nEven though we're talking about the future! Use IS, not will be! 🌤️"
        },
        {
          type: "mc",
          q: "First conditional talks about:",
          options: [
            "Real situations that are possible",
            "Imaginary situations that are impossible now",
            "Past situations"
          ],
          a: "Real situations that are possible",
          successtip: "✓ REAL POSSIBILITIES!\nThings that can actually happen! Not dreams or fantasies! 💼"
        },
        {
          type: "match",
          q: "Match the situations with their results:",
          pairs: [
            { left: "If I find my keys,", right: "I'll drive to work" },
            { left: "If she calls me,", right: "I'll answer" },
            { left: "If they arrive late,", right: "they'll miss the meeting" }
          ],
          a: "completed",
          successtip: "✓ LOGICAL CONNECTIONS!\nCondition → Result! Cause and effect in real situations! 🔗"
        },
        {
          type: "order",
          q: "Translate: Si llueve, nos quedaremos en casa",
          words: ["If", "it", "rains,", "we'll", "stay", "home"],
          a: "If it rains, we'll stay home",
          extra: ["will", "rained"],
          successtip: "✓ NATURAL ENGLISH!\nSimple, clear, and correct! This is how natives speak! 🏠"
        }
      ]
    },
    
    {
      id: "m9-u14-s2",
      title: "Set 2: First Conditional in Business 💼",
      context: `**Sales Strategy Discussion**

Lisa: "If we lower our prices, we'll attract more customers. But if we reduce quality, we'll lose our reputation!"`,
      
      questions: [
        {
          type: "mc",
          q: "Lisa is discussing:",
          options: [
            "Real business strategies they could actually try",
            "Impossible dreams",
            "Past business decisions"
          ],
          a: "Real business strategies they could actually try",
          tip: "These are real options the company can choose!",
          successtip: "✓ BUSINESS PLANNING!\nFirst conditional is perfect for discussing real strategies and possibilities! 📊"
        },
        {
          type: "match",
          q: "Match the business conditions with results:",
          pairs: [
            { left: "If we hire more staff,", right: "we'll complete projects faster" },
            { left: "If we advertise more,", right: "we'll increase sales" },
            { left: "If we cut costs,", right: "we'll save money" }
          ],
          a: "completed",
          successtip: "✓ BUSINESS LOGIC!\nCause and effect in the workplace! Real consequences! 💡"
        },
        {
          type: "input",
          q: "Complete: If we _____ (invest) in training, our team will improve.",
          a: "invest",
          alts: [],
          tip: "Present simple after IF!",
          successtip: "✓ BUSINESS INVESTMENT!\nTalking about real investment opportunities! Smart business talk! 💰"
        },
        {
          type: "order",
          q: "Translate: Si abrimos una nueva oficina, necesitaremos más empleados",
          words: ["If", "we", "open", "a", "new", "office,", "we'll", "need", "more", "employees"],
          a: "If we open a new office, we'll need more employees",
          extra: ["will", "opened"],
          successtip: "✓ EXPANSION PLANNING!\nTalking about real business growth! Logical consequences! 🏢"
        },
        {
          type: "mc",
          q: "Which is the correct business prediction?",
          options: [
            "If we launch the product, we'll make a profit",
            "If we will launch the product, we make a profit",
            "If we launched the product, we'll make a profit"
          ],
          a: "If we launch the product, we'll make a profit",
          successtip: "✓ PRODUCT LAUNCH!\nFirst conditional for real business plans! Present + will! 🚀"
        },
        {
          type: "order",
          q: "Translate: No conseguiremos el contrato si no mejoramos nuestra oferta",
          words: ["We", "won't", "get", "the", "contract", "if", "we", "don't", "improve", "our", "offer"],
          a: "We won't get the contract if we don't improve our offer",
          extra: ["will", "didn't"],
          successtip: "✓ COMPETITIVE BUSINESS!\nNegative first conditional! Real consequences of inaction! 📉"
        },
        {
          type: "input",
          q: "Complete: If the market _____ (crash), we'll lose money.",
          a: "crashes",
          alts: [],
          tip: "Third person singular!",
          successtip: "✓ MARKET RISKS!\nTalking about real business risks! Prepare for possibilities! 📈"
        },
        {
          type: "mc",
          q: "If you want to talk about a REAL business plan, use:",
          options: [
            "First conditional (If + present, will + verb)",
            "Second conditional (If + past, would + verb)",
            "Past simple"
          ],
          a: "First conditional (If + present, will + verb)",
          successtip: "✓ RIGHT CHOICE!\nFirst conditional = real plans that can happen! 🎯"
        },
        {
          type: "match",
          q: "Match the business scenarios:",
          pairs: [
            { left: "If we meet our targets,", right: "we'll get bonuses" },
            { left: "If sales increase,", right: "we'll hire more people" },
            { left: "If costs rise,", right: "we'll raise prices" }
          ],
          a: "completed",
          successtip: "✓ BUSINESS CYCLES!\nUnderstanding cause and effect in business! Real workplace situations! 🔄"
        },
        {
          type: "order",
          q: "Translate: Si el cliente aprueba, empezaremos el lunes",
          words: ["If", "the", "client", "approves,", "we'll", "start", "on", "Monday"],
          a: "If the client approves, we'll start on Monday",
          extra: ["will", "approved"],
          successtip: "✓ CLIENT RELATIONS!\nReal business scenarios! Waiting for approval to proceed! 🤝"
        }
      ]
    },

    {
      id: "m9-u14-s3",
      title: "Set 3: Introduction to Second Conditional 💭",
      context: `**Lunch Break Dream Talk**

Tom: "If I owned this company, I'd change everything! What would you do if you were the boss?"`,
      
      questions: [
        {
          type: "mc",
          q: "Tom is talking about:",
          options: [
            "An unreal/imaginary situation (he doesn't own the company)",
            "A real plan to buy the company",
            "Something that happened in the past"
          ],
          a: "An unreal/imaginary situation (he doesn't own the company)",
          tip: "He DOESN'T own it - it's just imagination!",
          successtip: "✓ SECOND CONDITIONAL!\nFor unreal, hypothetical, imaginary situations! Dreams and fantasies! 💭"
        },
        {
          type: "match",
          q: "Match the parts of second conditional:",
          pairs: [
            { left: "If I owned the company,", right: "I'd change everything" },
            { left: "If I were rich,", right: "I'd travel the world" },
            { left: "If she had time,", right: "she'd help us" }
          ],
          a: "completed",
          successtip: "✓ SECOND CONDITIONAL STRUCTURE!\nIf + past simple, would + base verb! For unreal situations! ✨"
        },
        {
          type: "input",
          q: "Complete: If I _____ (be) the boss, I'd give everyone a raise.",
          a: "were",
          alts: ["was"],
          tip: "Were is more formal, but 'was' is also used in modern English!",
          successtip: "✓ IF I WERE!\nTraditionally 'were' for all persons in second conditional! Both accepted! 👔"
        },
        {
          type: "order",
          q: "Translate: Si tuviera más dinero, compraría un coche nuevo",
          words: ["If", "I", "had", "more", "money,", "I'd", "buy", "a", "new", "car"],
          a: "If I had more money, I'd buy a new car",
          extra: ["have", "would", "will"],
          tip: "I'd = I would",
          successtip: "✓ UNREAL SITUATION!\nYou DON'T have more money now - it's hypothetical! Past tense + would! 🚗"
        },
        {
          type: "mc",
          q: "Which sentence is correct for an UNREAL situation?",
          options: [
            "If I won the lottery, I'd quit my job",
            "If I win the lottery, I'll quit my job",
            "If I would win the lottery, I quit my job"
          ],
          a: "If I won the lottery, I'd quit my job",
          tip: "You probably won't win - it's very unlikely!",
          successtip: "✓ SECOND CONDITIONAL!\nFor unlikely or impossible situations! Past + would! 🎰"
        },
        {
          type: "order",
          q: "Translate: ¿Qué harías si fueras millonario?",
          words: ["What", "would", "you", "do", "if", "you", "were", "a", "millionaire?"],
          a: "What would you do if you were a millionaire?",
          extra: ["will", "are"],
          successtip: "✓ HYPOTHETICAL QUESTION!\nAsking about unreal situations! Great for discussions! 💰"
        },
        {
          type: "input",
          q: "Complete: If she _____ (have) a car, she wouldn't take the bus.",
          a: "had",
          alts: [],
          tip: "She doesn't have a car - it's unreal!",
          successtip: "✓ PAST SIMPLE!\nUse past tense after IF in second conditional! Even for present situations! 🚌"
        },
        {
          type: "mc",
          q: "Second conditional is used for:",
          options: [
            "Unreal, imaginary, or very unlikely situations",
            "Real plans and possibilities",
            "Past events that already happened"
          ],
          a: "Unreal, imaginary, or very unlikely situations",
          successtip: "✓ IMAGINARY WORLD!\nDreams, fantasies, and 'what if' scenarios! Not real NOW! 🌟"
        },
        {
          type: "match",
          q: "Match the hypothetical situations:",
          pairs: [
            { left: "If I lived in Paris,", right: "I'd learn French" },
            { left: "If he had a degree,", right: "he'd get a better job" },
            { left: "If we knew the answer,", right: "we'd tell you" }
          ],
          a: "completed",
          successtip: "✓ UNREAL CONDITIONS!\nThey DON'T live in Paris, he DOESN'T have a degree! All hypothetical! 🌍"
        },
        {
          type: "order",
          q: "Translate: Si fuera más joven, viajaría más",
          words: ["If", "I", "were", "younger,", "I'd", "travel", "more"],
          a: "If I were younger, I'd travel more",
          extra: ["was", "will", "would"],
          successtip: "✓ AGE FANTASY!\nYou can't change your age - it's impossible! Perfect for second conditional! ✈️"
        }
      ]
    },

    {
      id: "m9-u14-s4",
      title: "Set 4: Second Conditional in Business Dreams 🌟",
      context: `**Coffee Break Fantasy**

Sarah: "If I had a million dollars, I'd start my own business. If I were CEO, I'd give everyone Fridays off!"`,
      
      questions: [
        {
          type: "mc",
          q: "Sarah's statements are:",
          options: [
            "Hypothetical dreams (she's not CEO and doesn't have a million)",
            "Real business plans",
            "Past experiences"
          ],
          a: "Hypothetical dreams (she's not CEO and doesn't have a million)",
          successtip: "✓ BUSINESS DREAMS!\nSecond conditional for career fantasies and 'what if' scenarios! 💭"
        },
        {
          type: "match",
          q: "Match the business fantasies:",
          pairs: [
            { left: "If I ran the company,", right: "I'd change the culture" },
            { left: "If we had more budget,", right: "we'd hire experts" },
            { left: "If she were manager,", right: "she'd reorganize everything" }
          ],
          a: "completed",
          successtip: "✓ WORKPLACE DREAMS!\nImagining different scenarios! Great for creative discussions! 💡"
        },
        {
          type: "input",
          q: "Complete: If we _____ (have) unlimited resources, we'd expand globally.",
          a: "had",
          alts: [],
          tip: "We DON'T have unlimited resources - it's hypothetical!",
          successtip: "✓ DREAM SCENARIO!\nTalking about ideal but unreal situations! Perfect second conditional! 🌍"
        },
        {
          type: "order",
          q: "Translate: Si fuera el jefe, reduciría las horas de trabajo",
          words: ["If", "I", "were", "the", "boss,", "I'd", "reduce", "working", "hours"],
          a: "If I were the boss, I'd reduce working hours",
          extra: ["was", "will", "am"],
          successtip: "✓ MANAGEMENT FANTASY!\nImagining yourself in power! Common office daydream! 👔"
        },
        {
          type: "mc",
          q: "Which expresses an UNREAL business situation?",
          options: [
            "If I owned a startup, I'd focus on innovation",
            "If I own a startup, I'll focus on innovation",
            "If I will own a startup, I focus on innovation"
          ],
          a: "If I owned a startup, I'd focus on innovation",
          successtip: "✓ ENTREPRENEUR DREAM!\nYou don't own a startup NOW - it's hypothetical! Second conditional! 🚀"
        },
        {
          type: "order",
          q: "Translate: Si tuviéramos más clientes, contrataríamos más personal",
          words: ["If", "we", "had", "more", "clients,", "we'd", "hire", "more", "staff"],
          a: "If we had more clients, we'd hire more staff",
          extra: ["have", "will"],
          successtip: "✓ GROWTH SCENARIO!\nImagining business expansion! Hypothetical but useful planning! 📈"
        },
        {
          type: "input",
          q: "Complete: If the economy _____ (be) better, we'd invest more.",
          a: "were",
          alts: ["was"],
          successtip: "✓ ECONOMIC WISHFUL THINKING!\nThe economy ISN'T better now - we're imagining! 💹"
        },
        {
          type: "mc",
          q: "In business English, second conditional is great for:",
          options: [
            "Discussing hypothetical scenarios and brainstorming",
            "Making definite plans",
            "Reporting past results"
          ],
          a: "Discussing hypothetical scenarios and brainstorming",
          successtip: "✓ CREATIVE THINKING!\nSecond conditional opens up discussion! Safe to explore ideas! 🧠"
        },
        {
          type: "match",
          q: "Match the business 'what ifs':",
          pairs: [
            { left: "If costs were lower,", right: "we'd be more competitive" },
            { left: "If I worked remotely,", right: "I'd be more productive" },
            { left: "If we merged,", right: "we'd dominate the market" }
          ],
          a: "completed",
          successtip: "✓ STRATEGIC THINKING!\nExploring possibilities without commitment! Business brainstorming! 🎯"
        },
        {
          type: "order",
          q: "Translate: Si pudiera elegir, trabajaría desde casa",
          words: ["If", "I", "could", "choose,", "I'd", "work", "from", "home"],
          a: "If I could choose, I'd work from home",
          extra: ["can", "will"],
          successtip: "✓ WORK PREFERENCE!\nBut you CAN'T choose right now! Expressing wishes with second conditional! 🏠"
        }
      ]
    },

    {
      id: "m9-u14-s5",
      title: "Set 5: Comparing First vs Second Conditional ⚖️",
      context: `**Performance Review**

Boss: "If you work hard this month, you'll get promoted. [REAL POSSIBILITY]
But if you were the manager, what would you change? [HYPOTHETICAL]"`,
      
      questions: [
        {
          type: "mc",
          q: "The first sentence (you'll get promoted) is:",
          options: [
            "A real possibility - it can actually happen",
            "An impossible dream",
            "Just a polite suggestion"
          ],
          a: "A real possibility - it can actually happen",
          tip: "First conditional = real and possible!",
          successtip: "✓ REAL OPPORTUNITY!\nFirst conditional for things that CAN actually happen! 🎯"
        },
        {
          type: "mc",
          q: "The second sentence (if you were the manager) is:",
          options: [
            "Hypothetical - you're NOT the manager now",
            "A real plan for next week",
            "About the past"
          ],
          a: "Hypothetical - you're NOT the manager now",
          successtip: "✓ IMAGINARY SCENARIO!\nSecond conditional because you're NOT the manager! Just asking opinion! 💭"
        },
        {
          type: "match",
          q: "Match REAL vs UNREAL situations:",
          pairs: [
            { left: "If it rains, I'll stay home", right: "REAL (might rain)" },
            { left: "If I were a bird, I'd fly", right: "UNREAL (impossible)" },
            { left: "If she calls, I'll answer", right: "REAL (she might call)" }
          ],
          a: "completed",
          successtip: "✓ KEY DIFFERENCE!\nFirst = CAN happen! Second = CAN'T or very unlikely! 🔑"
        },
        {
          type: "input",
          q: "Complete with FIRST conditional: If we _____ (finish) early, we'll celebrate.",
          a: "finish",
          alts: [],
          tip: "This is possible and real!",
          successtip: "✓ REAL PLAN!\nFirst conditional because finishing early is POSSIBLE! 🎉"
        },
        {
          type: "input",
          q: "Complete with SECOND conditional: If I _____ (be) you, I'd take the job.",
          a: "were",
          alts: ["was"],
          tip: "You can't BE another person - impossible!",
          successtip: "✓ IMPOSSIBLE SITUATION!\nYou can NEVER be another person! Classic second conditional! 👤"
        },
        {
          type: "order",
          q: "REAL situation: Si estudias, aprobarás",
          words: ["If", "you", "study,", "you'll", "pass"],
          a: "If you study, you'll pass",
          extra: ["studied", "would"],
          tip: "Use first conditional - this is possible!",
          successtip: "✓ ACHIEVABLE GOAL!\nStudying and passing is REAL and POSSIBLE! First conditional! 📚"
        },
        {
          type: "order",
          q: "UNREAL situation: Si fuera rico, viajaría por el mundo",
          words: ["If", "I", "were", "rich,", "I'd", "travel", "the", "world"],
          a: "If I were rich, I'd travel the world",
          extra: ["am", "will"],
          successtip: "✓ FANTASY!\nYou're NOT rich now! Second conditional for dreams! 🌍"
        },
        {
          type: "mc",
          q: "Choose the FIRST conditional:",
          options: [
            "If we leave now, we'll arrive on time",
            "If I were you, I'd leave now",
            "If I had a car, I'd drive"
          ],
          a: "If we leave now, we'll arrive on time",
          successtip: "✓ REAL CHOICE!\nLeaving now is a REAL option we can choose! Present + will! ⏰"
        },
        {
          type: "mc",
          q: "Choose the SECOND conditional:",
          options: [
            "If I lived in Tokyo, I'd learn Japanese",
            "If it rains tomorrow, I'll bring an umbrella",
            "If she arrives early, we'll start the meeting"
          ],
          a: "If I lived in Tokyo, I'd learn Japanese",
          tip: "You DON'T live in Tokyo!",
          successtip: "✓ HYPOTHETICAL LOCATION!\nYou don't live there NOW! Past + would! 🗾"
        },
        {
          type: "match",
          q: "Match the situations to the correct type:",
          pairs: [
            { left: "If I find my keys, I'll drive", right: "FIRST (real/possible)" },
            { left: "If I were invisible, I'd spy", right: "SECOND (impossible)" },
            { left: "If we win, we'll celebrate", right: "FIRST (real/possible)" }
          ],
          a: "completed",
          successtip: "✓ MASTER THE DIFFERENCE!\nFirst = REAL possibility! Second = UNREAL/impossible! You've got this! 🏆"
        }
      ]
    },

    {
      id: "m9-u14-s6",
      title: "Set 6: Business Collocations - GIVE 🤝",
      context: `**Strategy Meeting**

Director: "We need to give priority to customer satisfaction. Let me give this some thought before our next meeting."`,
      
      questions: [
        {
          type: "mc",
          q: "Give priority to means:",
          options: [
            "Make something the most important",
            "Give something away for free",
            "Forget about something"
          ],
          a: "Make something the most important",
          successtip: "✓ GIVE PRIORITY!\nMake it #1! The most important thing! Essential business phrase! 🥇"
        },
        {
          type: "match",
          q: "Match the GIVE collocations:",
          pairs: [
            { left: "Give priority to", right: "Make most important" },
            { left: "Give thought to", right: "Consider carefully" },
            { left: "Give help", right: "Provide assistance" }
          ],
          a: "completed",
          successtip: "✓ GIVE PHRASES!\nThese are fixed expressions in business English! Learn them together! 📊"
        },
        {
          type: "input",
          q: "Complete: We must give _____ to quality control. (make it most important)",
          a: "priority",
          alts: [],
          tip: "Give PRIORITY to = make it #1!",
          successtip: "✓ TOP PRIORITY!\nGive priority to + noun! What's most important in business! 🎯"
        },
        {
          type: "order",
          q: "Translate: Déjame pensar en esto",
          words: ["Let", "me", "give", "this", "some", "thought"],
          a: "Let me give this some thought",
          extra: ["think", "a"],
          tip: "Give thought = think carefully!",
          successtip: "✓ GIVE THOUGHT!\nPolite business way to say 'let me think about it'! Professional! 🤔"
        },
        {
          type: "mc",
          q: "In a meeting, 'Can you give me some help with this?' means:",
          options: [
            "Can you assist me?",
            "Can you give this to me?",
            "Can you pay for this?"
          ],
          a: "Can you assist me?",
          successtip: "✓ GIVE HELP!\nGive help = provide assistance! Common in collaborative work! 🤝"
        },
        {
          type: "input",
          q: "Complete: The CEO will give _____ to the new project. (make it most important)",
          a: "priority",
          alts: [],
          successtip: "✓ CEO DECISION!\nWhat gets priority gets resources and attention! Important! 💼"
        },
        {
          type: "order",
          q: "Translate: Necesitamos dar prioridad a la seguridad",
          words: ["We", "need", "to", "give", "priority", "to", "safety"],
          a: "We need to give priority to safety",
          extra: ["the", "a"],
          successtip: "✓ SAFETY FIRST!\nGive priority to + topic! No article needed! Natural business English! ⚠️"
        },
        {
          type: "mc",
          q: "Which is correct business English?",
          options: [
            "Let me give it some thought",
            "Let me make it some thought",
            "Let me do it some thought"
          ],
          a: "Let me give it some thought",
          successtip: "✓ FIXED PHRASE!\nGIVE thought (not make or do)! Memorize as a chunk! 💡"
        },
        {
          type: "match",
          q: "Match the business scenarios with GIVE phrases:",
          pairs: [
            { left: "Most important task?", right: "Give priority to it" },
            { left: "Need to consider?", right: "Give it some thought" },
            { left: "Colleague struggling?", right: "Give them help" }
          ],
          a: "completed",
          successtip: "✓ PRACTICAL USAGE!\nKnowing WHEN to use each phrase! Real business situations! 💼"
        },
        {
          type: "order",
          q: "Translate: ¿Puedes ayudarme con esto?",
          words: ["Can", "you", "give", "me", "some", "help", "with", "this?"],
          a: "Can you give me some help with this?",
          extra: ["a", "the"],
          successtip: "✓ ASKING FOR HELP!\nPolite and professional! 'Give some help' sounds natural! 🙋"
        }
      ]
    },

    {
      id: "m9-u14-s7",
      title: "Set 7: Business Collocations - HOLD 📅",
      context: `**Executive Planning Session**

VP: "Let's hold talks with the investors next week. We should hold a meeting to discuss the merger."`,
      
      questions: [
        {
          type: "mc",
          q: "Hold talks means:",
          options: [
            "Have formal discussions or negotiations",
            "Carry someone's conversation",
            "Stop people from talking"
          ],
          a: "Have formal discussions or negotiations",
          successtip: "✓ HOLD TALKS!\nFormal business discussions! Negotiations with important people! 💬"
        },
        {
          type: "match",
          q: "Match the HOLD collocations:",
          pairs: [
            { left: "Hold talks", right: "Have negotiations" },
            { left: "Hold a meeting", right: "Organize a meeting" },
            { left: "Hold discussions", right: "Have formal talks" }
          ],
          a: "completed",
          successtip: "✓ HOLD PHRASES!\nHOLD is used for organizing formal business events! 🏢"
        },
        {
          type: "input",
          q: "Complete: We need to hold a _____ to discuss the budget. (organized gathering)",
          a: "meeting",
          alts: [],
          tip: "Hold a meeting = organize/have a meeting!",
          successtip: "✓ HOLD A MEETING!\nStandard phrase! More formal than 'have a meeting'! 📋"
        },
        {
          type: "order",
          q: "Translate: Celebremos una reunión el lunes",
          words: ["Let's", "hold", "a", "meeting", "on", "Monday"],
          a: "Let's hold a meeting on Monday",
          extra: ["make", "do"],
          successtip: "✓ SCHEDULING!\nHold a meeting + time! Professional way to organize! 📆"
        },
        {
          type: "mc",
          q: "Hold off (on) means:",
          options: [
            "Delay or postpone something",
            "Start something immediately",
            "Cancel something permanently"
          ],
          a: "Delay or postpone something",
          tip: "Hold off = wait, delay!",
          successtip: "✓ HOLD OFF!\nPostpone a decision! 'Let's hold off on that' = Let's wait! ⏸️"
        },
        {
          type: "input",
          q: "Complete: The government will hold _____ with labor unions. (negotiations)",
          a: "talks",
          alts: ["discussions"],
          successtip: "✓ GOVERNMENT BUSINESS!\nHold talks/discussions for formal negotiations! Both work! 🏛️"
        },
        {
          type: "order",
          q: "Translate: Deberíamos posponer esa decisión",
          words: ["We", "should", "hold", "off", "on", "that", "decision"],
          a: "We should hold off on that decision",
          extra: ["of", "about"],
          tip: "Hold off ON something!",
          successtip: "✓ DELAY DECISION!\nHold off on + noun! Smart business move when uncertain! 🤔"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Let's hold discussions about the merger",
            "Let's make discussions about the merger",
            "Let's do discussions about the merger"
          ],
          a: "Let's hold discussions about the merger",
          successtip: "✓ HOLD DISCUSSIONS!\nNot make or do! Fixed collocation in business English! 🤝"
        },
        {
          type: "match",
          q: "Match the situations with HOLD phrases:",
          pairs: [
            { left: "Negotiate with partners?", right: "Hold talks" },
            { left: "Organize team gathering?", right: "Hold a meeting" },
            { left: "Wait before deciding?", right: "Hold off" }
          ],
          a: "completed",
          successtip: "✓ STRATEGIC LANGUAGE!\nKnowing which HOLD phrase fits each situation! Pro level! 🎯"
        },
        {
          type: "order",
          q: "Translate: Vamos a celebrar conversaciones con el cliente",
          words: ["We're", "going", "to", "hold", "talks", "with", "the", "client"],
          a: "We're going to hold talks with the client",
          extra: ["make", "have"],
          successtip: "✓ CLIENT RELATIONS!\nHold talks with + person/organization! Professional! 💼"
        }
      ]
    },

    {
      id: "m9-u14-s8",
      title: "Set 8: Business Collocations - SET 🎯",
      context: `**Goal-Setting Workshop**

Trainer: "It's important to set clear goals for your team. We need to set limits on overtime work."`,
      
      questions: [
        {
          type: "mc",
          q: "Set goals means:",
          options: [
            "Establish objectives or targets",
            "Put goals in a specific place",
            "Forget about goals"
          ],
          a: "Establish objectives or targets",
          successtip: "✓ SET GOALS!\nDefine what you want to achieve! Essential for success! 🎯"
        },
        {
          type: "match",
          q: "Match the SET collocations:",
          pairs: [
            { left: "Set goals", right: "Establish targets" },
            { left: "Set limits", right: "Establish boundaries" },
            { left: "Set a precedent", right: "Create an example for future" }
          ],
          a: "completed",
          successtip: "✓ SET PHRASES!\nSET = establish, create, define! Powerful business vocabulary! 💪"
        },
        {
          type: "input",
          q: "Complete: We need to set _____ on working hours. (boundaries)",
          a: "limits",
          alts: [],
          tip: "Set limits = establish boundaries!",
          successtip: "✓ SET LIMITS!\nHealthy boundaries in the workplace! Important for balance! ⚖️"
        },
        {
          type: "order",
          q: "Translate: Necesitamos establecer objetivos claros",
          words: ["We", "need", "to", "set", "clear", "goals"],
          a: "We need to set clear goals",
          extra: ["make", "do"],
          successtip: "✓ CLARITY!\nSet clear/specific goals! More effective than vague ones! 🔍"
        },
        {
          type: "mc",
          q: "Set a precedent means:",
          options: [
            "Create an example that will influence future decisions",
            "Set a present on the table",
            "Remember the past"
          ],
          a: "Create an example that will influence future decisions",
          successtip: "✓ SET A PRECEDENT!\nFirst action that becomes the standard! Important in law and business! ⚖️"
        },
        {
          type: "input",
          q: "Complete: The manager set ambitious _____ for the quarter. (objectives)",
          a: "targets",
          alts: ["goals"],
          tip: "Set goals/targets - both work!",
          successtip: "✓ AMBITIOUS TARGETS!\nSet goals OR set targets! Both are perfect! 📈"
        },
        {
          type: "order",
          q: "Translate: No queremos sentar un mal precedente",
          words: ["We", "don't", "want", "to", "set", "a", "bad", "precedent"],
          a: "We don't want to set a bad precedent",
          extra: ["make", "the"],
          successtip: "✓ CAREFUL DECISIONS!\nBad precedents cause future problems! Think ahead! 🚨"
        },
        {
          type: "mc",
          q: "Which is correct business English?",
          options: [
            "Let's set realistic targets",
            "Let's make realistic targets",
            "Let's do realistic targets"
          ],
          a: "Let's set realistic targets",
          successtip: "✓ SET TARGETS!\nOnly SET works with targets/goals! Fixed collocation! 🎯"
        },
        {
          type: "match",
          q: "Match the business needs with SET phrases:",
          pairs: [
            { left: "Need clear objectives?", right: "Set goals" },
            { left: "Need boundaries?", right: "Set limits" },
            { left: "Making historic decision?", right: "Set a precedent" }
          ],
          a: "completed",
          successtip: "✓ STRATEGIC PLANNING!\nSET vocabulary for defining direction and boundaries! 🧭"
        },
        {
          type: "order",
          q: "Translate: Cada empleado debe establecer sus propios objetivos",
          words: ["Each", "employee", "should", "set", "their", "own", "goals"],
          a: "Each employee should set their own goals",
          extra: ["make", "his"],
          tip: "'Their' is now accepted for singular 'each employee'!",
          successtip: "✓ PERSONAL GOALS!\nSelf-directed goal-setting! Empowers employees! 💼"
        }
      ]
    },

    {
      id: "m9-u14-s9",
      title: "Set 9: Mixed Conditionals in Business 🔀",
      context: `**Startup Pitch Meeting**

Investor: "If your product works, I'll invest $500k. [FIRST - real possibility]
But if I were you, I'd focus on marketing first. [SECOND - giving advice]"`,
      
      questions: [
        {
          type: "mc",
          q: "The investor uses FIRST conditional because:",
          options: [
            "Investing is a REAL possibility if the product works",
            "It's just a dream",
            "It happened in the past"
          ],
          a: "Investing is a REAL possibility if the product works",
          successtip: "✓ REAL INVESTMENT!\nFirst conditional for real business decisions! Actual money! 💰"
        },
        {
          type: "mc",
          q: "The investor uses SECOND conditional because:",
          options: [
            "He's NOT you - it's hypothetical advice",
            "He will become you",
            "It's a real plan"
          ],
          a: "He's NOT you - it's hypothetical advice",
          successtip: "✓ IF I WERE YOU!\nClassic advice phrase! Second conditional for suggestions! 💡"
        },
        {
          type: "input",
          q: "FIRST conditional: If we _____ (launch) next month, we'll capture the market.",
          a: "launch",
          alts: [],
          tip: "Real plan - they CAN launch!",
          successtip: "✓ REAL LAUNCH PLAN!\nActual business strategy! First conditional for possible actions! 🚀"
        },
        {
          type: "input",
          q: "SECOND conditional: If I _____ (have) more experience, I'd apply for CEO.",
          a: "had",
          alts: [],
          tip: "You DON'T have enough experience now!",
          successtip: "✓ LACKING EXPERIENCE!\nYou don't have it NOW - hypothetical! Second conditional! 📚"
        },
        {
          type: "order",
          q: "REAL: Si consiguen la inversión, expandirán a Asia",
          words: ["If", "they", "get", "the", "investment,", "they'll", "expand", "to", "Asia"],
          a: "If they get the investment, they'll expand to Asia",
          extra: ["got", "would"],
          successtip: "✓ REAL EXPANSION!\nGetting investment is POSSIBLE! Real consequence! 🌏"
        },
        {
          type: "order",
          q: "UNREAL: Si yo fuera el director, haría cambios grandes",
          words: ["If", "I", "were", "the", "director,", "I'd", "make", "big", "changes"],
          a: "If I were the director, I'd make big changes",
          extra: ["am", "will"],
          successtip: "✓ MANAGEMENT FANTASY!\nYou're NOT the director! Imagining power! 👔"
        },
        {
          type: "mc",
          q: "Choose the sentence giving ADVICE:",
          options: [
            "If I were you, I'd negotiate harder",
            "If you negotiate harder, you'll get a better deal",
            "If you negotiated harder, you got a better deal"
          ],
          a: "If I were you, I'd negotiate harder",
          tip: "Advice uses 'If I were you'!",
          successtip: "✓ GIVING ADVICE!\n'If I were you' = I'm suggesting what YOU should do! 🗣️"
        },
        {
          type: "match",
          q: "Match REAL vs ADVICE:",
          pairs: [
            { left: "If you call them, they'll respond", right: "REAL (first)" },
            { left: "If I were you, I'd call them", right: "ADVICE (second)" },
            { left: "If we try, we'll succeed", right: "REAL (first)" }
          ],
          a: "completed",
          successtip: "✓ MIXING CONDITIONALS!\nFirst for REAL plans! Second for advice and dreams! 🎯"
        },
        {
          type: "mc",
          q: "Which talks about a REAL business decision?",
          options: [
            "If we lower prices, we'll gain customers",
            "If I owned the company, I'd lower prices",
            "If we lowered prices, we'd gain customers"
          ],
          a: "If we lower prices, we'll gain customers",
          tip: "They CAN actually lower prices!",
          successtip: "✓ REAL STRATEGY!\nThey have the power to do this! First conditional! 📊"
        },
        {
          type: "order",
          q: "Combine: Si trabajas duro (REAL), tendrás éxito",
          words: ["If", "you", "work", "hard,", "you'll", "succeed"],
          a: "If you work hard, you'll succeed",
          extra: ["worked", "would"],
          successtip: "✓ MOTIVATIONAL!\nReal advice with real consequences! Work = success! 🏆"
        }
      ]
    },

    {
      id: "m9-u14-s10",
      title: "Set 10: Talking About Real & Unreal Possibilities 💬",
      context: `**Career Counseling Session**

Counselor: "If you apply for the job, you might get it. [POSSIBLE]
If you had more experience, you'd be perfect. [NOT POSSIBLE NOW]"`,
      
      questions: [
        {
          type: "mc",
          q: "Applying for the job is:",
          options: [
            "A real option - you CAN do it",
            "Impossible right now",
            "Something from the past"
          ],
          a: "A real option - you CAN do it",
          successtip: "✓ REAL OPPORTUNITY!\nYou have the ability to apply! First conditional territory! 📝"
        },
        {
          type: "mc",
          q: "Having more experience is:",
          options: [
            "Not possible right now - you need time",
            "Easy to get today",
            "Already achieved"
          ],
          a: "Not possible right now - you need time",
          successtip: "✓ TIME NEEDED!\nExperience takes time! Can't get it instantly! Second conditional! ⏳"
        },
        {
          type: "match",
          q: "Match the possibility level:",
          pairs: [
            { left: "If I study hard, I'll pass", right: "VERY POSSIBLE" },
            { left: "If I won the lottery, I'd quit", right: "VERY UNLIKELY" },
            { left: "If I were a genius, I'd invent something", right: "IMPOSSIBLE" }
          ],
          a: "completed",
          successtip: "✓ PROBABILITY SCALE!\nFirst conditional = possible! Second conditional = unlikely/impossible! 📊"
        },
        {
          type: "input",
          q: "POSSIBLE: If I _____ (try) harder, I'll improve. (you CAN try)",
          a: "try",
          alts: [],
          successtip: "✓ IN YOUR CONTROL!\nYou have the power to try harder! Real possibility! 💪"
        },
        {
          type: "input",
          q: "UNLIKELY: If I _____ (speak) perfect Chinese, I'd work in Beijing. (you don't speak it)",
          a: "spoke",
          alts: [],
          tip: "You DON'T speak perfect Chinese now!",
          successtip: "✓ LANGUAGE BARRIER!\nLearning perfect Chinese takes years! Hypothetical for now! 🇨🇳"
        },
        {
          type: "order",
          q: "REAL: Si me esfuerzo, lo conseguiré",
          words: ["If", "I", "make", "an", "effort,", "I'll", "achieve", "it"],
          a: "If I make an effort, I'll achieve it",
          extra: ["made", "would"],
          successtip: "✓ EMPOWERING!\nSuccess is in YOUR hands! Real possibility! First conditional! 🌟"
        },
        {
          type: "order",
          q: "UNREAL: Si tuviera superpoderes, salvaría el mundo",
          words: ["If", "I", "had", "superpowers,", "I'd", "save", "the", "world"],
          a: "If I had superpowers, I'd save the world",
          extra: ["have", "will"],
          tip: "Superpowers are impossible!",
          successtip: "✓ SUPERHERO FANTASY!\nImpossible situation! Perfect second conditional! 🦸"
        },
        {
          type: "mc",
          q: "Which expresses something POSSIBLE?",
          options: [
            "If I save money, I'll buy a car",
            "If I had a million dollars, I'd buy a car",
            "If I were rich, I'd buy a car"
          ],
          a: "If I save money, I'll buy a car",
          successtip: "✓ ACHIEVABLE GOAL!\nSaving money is POSSIBLE! First conditional for real goals! 🚗"
        },
        {
          type: "match",
          q: "Match the career scenarios:",
          pairs: [
            { left: "If I apply, I might get hired", right: "POSSIBLE (do it!)" },
            { left: "If I were the boss, I'd hire myself", right: "FANTASY (not possible)" },
            { left: "If I work hard, I'll get promoted", right: "POSSIBLE (realistic)" }
          ],
          a: "completed",
          successtip: "✓ CAREER PLANNING!\nFirst conditional for REAL career moves! Second for fantasies! 💼"
        },
        {
          type: "order",
          q: "Motivational: Si crees en ti mismo, alcanzarás tus metas",
          words: ["If", "you", "believe", "in", "yourself,", "you'll", "reach", "your", "goals"],
          a: "If you believe in yourself, you'll reach your goals",
          extra: ["believed", "would"],
          successtip: "✓ BELIEVE & ACHIEVE!\nBelieving is POSSIBLE! Real path to success! First conditional! 🏆"
        }
      ]
    },

    {
      id: "m9-u14-s11",
      title: "Set 11: READING - Business Expansion Email 📧",
      context: `**Email from Regional Manager**

From: Jennifer Martinez, Regional Manager
To: All Department Heads
Subject: Q2 Expansion Plans & Strategic Thinking

Dear Team,

I'm writing to discuss our expansion plans and get your input on some strategic scenarios.

**REAL PLANS (What we're actually doing):**
If we secure the investment next month, we'll open three new locations in Q2. If the board approves our budget, we'll hire 50 new employees. We'll hold talks with potential partners in Tokyo if the legal team gives us the green light.

**STRATEGIC THINKING (Hypothetical scenarios):**
Now, I want your creative input. If you were the CEO, what would you do differently? If we had unlimited budget, what markets would you prioritize? If you could set just three goals for the company, what would they be?

**Action Items:**
1. If you have concerns about the expansion, please hold discussions with your teams
2. We need to give priority to customer service during this growth phase
3. Let's set realistic limits on spending until we secure the funding

Please give this some thought and respond by Friday. If I were you, I'd involve your whole team in this brainstorming!

Best regards,
Jennifer Martinez
Regional Manager`,
      
      questions: [
        {
          type: "mc",
          q: "Opening three new locations is:",
          options: [
            "A real plan that depends on getting investment",
            "Just a dream with no real possibility",
            "Already completed"
          ],
          a: "A real plan that depends on getting investment",
          successtip: "✓ REAL BUSINESS PLAN!\nFirst conditional = real strategy! Depends on investment! 🏢"
        },
        {
          type: "mc",
          q: "When Jennifer asks 'If you were the CEO, what would you do?', she's:",
          options: [
            "Asking for creative hypothetical ideas",
            "Offering them the CEO position",
            "Talking about the past"
          ],
          a: "Asking for creative hypothetical ideas",
          successtip: "✓ BRAINSTORMING!\nSecond conditional for creative thinking! Safe to share ideas! 💡"
        },
        {
          type: "mc",
          q: "How many new employees will they hire?",
          options: [
            "50, if the board approves the budget",
            "50, definitely",
            "They won't hire anyone"
          ],
          a: "50, if the board approves the budget",
          tip: "It depends on approval!",
          successtip: "✓ CONDITIONAL HIRING!\nReal plan, but not 100% certain! Depends on conditions! 👥"
        },
        {
          type: "mc",
          q: "'Give priority to customer service' uses which business verb?",
          options: [
            "GIVE",
            "HOLD",
            "SET"
          ],
          a: "GIVE",
          successtip: "✓ GIVE PRIORITY!\nMake it most important! Business collocation with GIVE! 🥇"
        },
        {
          type: "mc",
          q: "'Hold talks with partners' means:",
          options: [
            "Have formal negotiations",
            "Stop talking to partners",
            "Carry their conversations"
          ],
          a: "Have formal negotiations",
          successtip: "✓ HOLD TALKS!\nFormal business discussions! HOLD collocation! 🤝"
        },
        {
          type: "mc",
          q: "'Set realistic limits' uses which business verb?",
          options: [
            "SET",
            "GIVE",
            "HOLD"
          ],
          a: "SET",
          successtip: "✓ SET LIMITS!\nEstablish boundaries! SET collocation! ⚖️"
        },
        {
          type: "mc",
          q: "Which sentence uses FIRST conditional?",
          options: [
            "If we secure the investment, we'll open new locations",
            "If you were the CEO, what would you do?",
            "If we had unlimited budget, what would you prioritize?"
          ],
          a: "If we secure the investment, we'll open new locations",
          tip: "Real possibility!",
          successtip: "✓ REAL PLAN!\nThey might actually secure it! First conditional! 💰"
        },
        {
          type: "mc",
          q: "Which sentence uses SECOND conditional?",
          options: [
            "If we had unlimited budget, what would you prioritize?",
            "If the board approves, we'll hire 50 employees",
            "If you have concerns, hold discussions"
          ],
          a: "If we had unlimited budget, what would you prioritize?",
          tip: "They DON'T have unlimited budget!",
          successtip: "✓ HYPOTHETICAL BUDGET!\nThey don't have it - just imagining! Second conditional! 💭"
        },
        {
          type: "mc",
          q: "Jennifer wants responses by:",
          options: [
            "Friday",
            "Next month",
            "Q2"
          ],
          a: "Friday",
          successtip: "✓ DEADLINE!\nClear deadline given! Good project management! 📅"
        },
        {
          type: "mc",
          q: "The overall tone of the email is:",
          options: [
            "Professional but encouraging creative input",
            "Very strict and authoritarian",
            "Casual and unprofessional"
          ],
          a: "Professional but encouraging creative input",
          successtip: "✓ BALANCED LEADERSHIP!\nMixing real plans with creative thinking! Great management! 🌟"
        }
      ]
    },

    {
      id: "m9-u14-s12",
      title: "Set 12: UNIT REVIEW - Real vs Unreal Situations 🎓",
      context: `**Complete Review - Unit 14**

Test your knowledge of First Conditional, Second Conditional, and Business Collocations!`,
      
      questions: [
        {
          type: "mc",
          q: "Which is FIRST conditional?",
          options: [
            "If we work hard, we'll succeed",
            "If we worked hard, we'd succeed",
            "If we would work hard, we succeed"
          ],
          a: "If we work hard, we'll succeed",
          tip: "If + present, will + verb",
          successtip: "✓ FIRST CONDITIONAL MASTER!\nReal possibility! If + present, will + verb! 🎯"
        },
        {
          type: "mc",
          q: "Which is SECOND conditional?",
          options: [
            "If I were rich, I'd travel the world",
            "If I'm rich, I'll travel the world",
            "If I will be rich, I travel the world"
          ],
          a: "If I were rich, I'd travel the world",
          tip: "If + past, would + verb",
          successtip: "✓ SECOND CONDITIONAL MASTER!\nUnreal situation! If + past, would + verb! 💭"
        },
        {
          type: "match",
          q: "Match the conditional types:",
          pairs: [
            { left: "If it rains, I'll stay home", right: "FIRST (possible)" },
            { left: "If I were you, I'd apply", right: "SECOND (advice)" },
            { left: "If she calls, I'll answer", right: "FIRST (possible)" }
          ],
          a: "completed",
          successtip: "✓ CONDITIONAL EXPERT!\nYou know the difference! Real vs Unreal! 🏆"
        },
        {
          type: "input",
          q: "Business: We need to give _____ to quality. (make it most important)",
          a: "priority",
          alts: [],
          successtip: "✓ GIVE PRIORITY!\nBusiness collocation mastered! GIVE + priority! 🥇"
        },
        {
          type: "input",
          q: "Business: Let's hold _____ with the investors. (negotiations)",
          a: "talks",
          alts: ["discussions"],
          successtip: "✓ HOLD TALKS!\nBusiness collocation mastered! HOLD + talks/discussions! 🤝"
        },
        {
          type: "input",
          q: "Business: We should set clear _____. (objectives)",
          a: "goals",
          alts: ["targets"],
          successtip: "✓ SET GOALS!\nBusiness collocation mastered! SET + goals/targets! 🎯"
        },
        {
          type: "order",
          q: "FIRST: Si estudiamos, aprobaremos",
          words: ["If", "we", "study,", "we'll", "pass"],
          a: "If we study, we'll pass",
          extra: ["studied", "would"],
          successtip: "✓ REAL POSSIBILITY!\nStudying and passing is achievable! First conditional! 📚"
        },
        {
          type: "order",
          q: "SECOND: Si fuera tú, aceptaría el trabajo",
          words: ["If", "I", "were", "you,", "I'd", "accept", "the", "job"],
          a: "If I were you, I'd accept the job",
          extra: ["am", "will"],
          successtip: "✓ GIVING ADVICE!\nClassic second conditional for suggestions! 💡"
        },
        {
          type: "mc",
          q: "Complete: We need to ___ priority to customer service.",
          options: [
            "give",
            "hold",
            "set"
          ],
          a: "give",
          successtip: "✓ GIVE PRIORITY!\nOnly GIVE works with priority! Business collocation! 🥇"
        },
        {
          type: "mc",
          q: "You've completed Unit 14! What did you learn?",
          options: [
            "First conditional (real), Second conditional (unreal), Business collocations!",
            "Only grammar",
            "Only vocabulary"
          ],
          a: "First conditional (real), Second conditional (unreal), Business collocations!",
          successtip: "✓ UNIT 14 COMPLETE! 🎉\nYou can now talk about real plans AND hypothetical dreams! Plus professional business vocabulary! You're ready for real business English! Keep practicing! 🚀💼"
        }
      ]
    }
  ]
};
